import React, { useState } from "react";
import { Button, Container, Row, Form, Alert } from "react-bootstrap";
import { runAPI } from "../utils/API/apiRunner";
import { getSettings } from "../utils/API/_AdminApiModules";
import { getDefaultStartDate, getDefaultEndDate } from "../utils/getTime";
import Papa from "papaparse";
import { whitelistedAffs } from "../utils/whitelist.js";
import LoadingOverlay from "../components/LoadingOverlay";
import ColumnMapTable from "../components/tables/columnMapTable";
import { getReportTexts } from "../utils/getTime";
import DateRangePicker from "../components/forms/DateRangePicker";

const MarkKalbachReport = () => {
	const [filteredData, setFilteredData] = useState([]);
	const [error, setError] = useState(null);
	const [loadingStage, setLoadingStage] = useState("");
	const [excludeRecruited, setExcludeRecruited] = useState(true);
	const exclusionList = whitelistedAffs;
	const [startDate, setStartDate] = useState(getDefaultStartDate("14days"));
	const [endDate, setEndDate] = useState(getDefaultEndDate());
	const [completedModules, setCompletedModules] = useState([]);
	const [loading, setLoading] = useState(false);
	const [stage, setStage] = useState("input");
	const [tableButton, setTableButton] = useState(<div></div>);
	const [warningReport, setWarningReport] = useState([]);
	const [minimumID, setMinimumID] = useState(300000);
	const [minimumSaleAvg, setMinimumSaleAvg] = useState(35);
	const [outlierBenchmark, setOutlierBenchmark] = useState(2000);
	//Static Consts
	const settings = getSettings();

	// Guess an order ID format from a merchant's order IDs.
	// Returns { format: string, firstDigits: string }
	function detectOrderFormat(orderIds) {
		if (!orderIds || orderIds.length === 0)
			return { format: "unknown", firstDigits: "" };

		// Use the first non-empty ID as a sample
		const sample = String(orderIds.find((x) => x)?.trim() || "");

		// Common patterns
		const rePlainDigits = /^(\d{5,})$/; // 5+ digits
		const reHashDigits = /^#(\d{3,})$/; // #12345
		const reAlphaNumDash = /^[A-Za-z0-9\-]+$/; // letters/numbers/dashes

		let format = "unknown";
		if (rePlainDigits.test(sample)) {
			format = `<${sample.length} digits>`;
		} else if (reHashDigits.test(sample)) {
			const digits = sample.replace("#", "");
			format = `#<${digits.length} digits>`;
		} else if (reAlphaNumDash.test(sample)) {
			// quick heuristic: LETTERS-NUMBERS or mixed
			const parts = sample.split("-");
			if (parts.length === 2) {
				const [p1, p2] = parts;
				if (/^[A-Za-z]+$/.test(p1) && /^\d+$/.test(p2))
					format = `[A-Z]+-<${p2.length} digits>`;
				else if (/^\d+$/.test(p1) && /^[A-Za-z]+$/.test(p2))
					format = `<${p1.length} digits>-[A-Z]+`;
				else format = "alphanumeric-dash";
			} else {
				format = "alphanumeric";
			}
		}

		// First 3–4 digits if any
		const firstDigits =
			sample.match(/\d{3,4}/)?.[0] ||
			sample.replace(/\D/g, "").slice(0, 4) ||
			"";

		return { format, firstDigits };
	}

	// Summarize orders by merchant with counts per dollar amount.
	// Returns array: [{ merchantId, merchantName, orderIds, amountCounts(Map), format, firstDigits }]
	function summarizeByMerchant(orders) {
		const byMerchant = {};
		for (const o of orders) {
			const merchantId = o["Merchant Id"];
			const merchantName = o["Merchant"];
			const orderId = o["Order Id"];
			const amount = Number.parseFloat(o["Transaction Amount"]).toFixed(
				2,
			);

			if (!byMerchant[merchantId]) {
				byMerchant[merchantId] = {
					merchantId,
					merchantName,
					orderIds: [],
					amountCounts: new Map(), // amount -> count
				};
			}
			const m = byMerchant[merchantId];
			m.orderIds.push(orderId);
			m.amountCounts.set(amount, (m.amountCounts.get(amount) || 0) + 1);
		}

		// add detected formats
		return Object.values(byMerchant)
			.map((m) => {
				const { format, firstDigits } = detectOrderFormat(m.orderIds);
				return { ...m, format, firstDigits };
			})
			.sort((a, b) => a.merchantName.localeCompare(b.merchantName));
	}
	async function fetchMerchantNormalFormat({
		merchantId,
		settings,
		endDate,
	}) {
		const res = await runAPI(
			{
				report_id: 8,
				startDate: getDefaultStartDate("last7days"),
				endDate,
			},
			settings.key,
			[merchantId], // note: array as your signature shows
		);

		const orderIds = (res || []).map((r) => r["Order Id"]).filter(Boolean);

		return detectOrderFormat(orderIds);
	}

	const runMarkKReport = async (e) => {
		//TODO set update this to be its own seperate script file.
		const settings = await getSettings();
		setLoading(true);
		console.log(startDate, endDate);
		const currentDates = getReportTexts(startDate, endDate);
		const mList = [];
		const warningData = [];
		const normalFormatCache = {};
		const affList = await runAPI(
			{
				report_id: 15,
				startDate: getDefaultStartDate("last30days"),
				endDate,
				merchantId: 0,
			},
			settings.key,
			"NULL",
		);

		try {
			const filtered = affList.filter((row) => {
				const affId = parseInt(row["Affiliate Id"]);
				const numSales = parseFloat(row["# of Sales"]);
				const tags = row["Affiliate Tags"] || "";
				const inExclusionList = exclusionList.includes(affId);
				const avgSale = parseFloat(row["Average Sale Amount"]);
				const recruited = tags
					.trim()
					.startsWith("Affiliate Recruiting");

				return (
					!isNaN(affId) &&
					affId >= minimumID &&
					!isNaN(numSales) &&
					numSales > 1 &&
					!isNaN(avgSale) &&
					avgSale > minimumSaleAvg &&
					!inExclusionList &&
					(!excludeRecruited || !recruited)
				);
			});
			const trimmed = filtered.map((row) => ({
				Affiliate_Id: row["Affiliate Id"],
				Affiliate: row["Affiliate"],
				Affiliate_Name: row["Affiliate Name"],
				Click_Throughs: row["Click Throughs"],
				Sales: row["Sales"],
				Num_Sales: row["# of Sales"],
				Conversion_Rate: row["Conversion Rate"],
				Average_Sale_Amount: row["Average Sale Amount"],
			}));

			setError(null);
			const warningList = {};
			const reviewList = {};

			for (let i = 0; i < trimmed.length; i++) {
				const affiliate_Id = trimmed[i].Affiliate_Id;
				const affiliate_Name = trimmed[i].Affiliate_Name;
				const result = await runAPI(
					{
						report_id: 8,
						startDate: startDate,
						endDate: endDate,
						affiliate_id: affiliate_Id,
					},
					settings.key,
					"NULL",
				);
				// TODO
				console.log(`Result for Affiliate ID ${affiliate_Id}:`, result);
				const merchantMap = {};

				//AFFAPIRETURN
				result.forEach((order) => {
					const merchantId = order["Merchant Id"];
					if (!mList.includes(merchantId)) {
						mList.push(merchantId);
					}

					const amount = parseFloat(order["Transaction Amount"]);
					const itemCount = parseInt(order["Item Count"]);
					const orderId = order["Order Id"];

					if (!merchantMap[merchantId]) {
						merchantMap[merchantId] = {
							orders: [],
							susTotals: {},
						};
					}

					merchantMap[merchantId].orders.push(orderId);

					const key = `${amount.toFixed(2)}|${itemCount}`;
					merchantMap[merchantId].susTotals[key] =
						(merchantMap[merchantId].susTotals[key] || 0) + 1;
				});

				let hasSuspicious = false;
				let totalMatchCount = 0;

				Object.entries(merchantMap).forEach(([merchantId, data]) => {
					Object.entries(data.susTotals).forEach(([key, count]) => {
						if (count >= 3) {
							hasSuspicious = true;
							totalMatchCount += count;
						}
					});
				});

				if (hasSuspicious) {
					const merchants = summarizeByMerchant(result);

					// Build blocks with NORMAL format appended (last 7d),
					// and style red+bold if different from the affiliate’s observed format.
					let rowHasDiff = false;
					let totalOrderCount = 0;

					const merchantBlocks = (
						await Promise.all(
							merchants.map(async (m) => {
								const entries = Array.from(
									m.amountCounts.entries(),
								)
									.filter(([, count]) => count >= 2)
									.sort((a, b) => b[1] - a[1]);

								if (entries.length === 0) return null;

								// get normal format
								let normal = normalFormatCache[m.merchantId];
								if (!normal) {
									try {
										normal =
											await fetchMerchantNormalFormat({
												merchantId: m.merchantId,
												settings,
												endDate,
											});
										normalFormatCache[m.merchantId] =
											normal;
									} catch {
										normal = {
											format: "unknown",
											firstDigits: "",
										};
									}
								}

								const isDiff =
									(normal?.format || "unknown") !==
									(m?.format || "unknown");
								if (isDiff) rowHasDiff = true;

								// accumulate total orders (count of all amounts)
								totalOrderCount += entries.reduce(
									(sum, [, count]) => sum + count,
									0,
								);

								return (
									<div
										key={m.merchantId}
										style={{
											marginBottom: 8,
											fontWeight: isDiff
												? 700
												: undefined,
											color: isDiff
												? "#c62828"
												: undefined,
										}}
									>
										<span>
											<strong>{m.merchantId}</strong> —{" "}
											{m.merchantName} &nbsp;
											<em>
												(Observed Format: {m.format}
												{m.firstDigits
													? `, first digits: ${m.firstDigits}`
													: ""}
												)
											</em>
										</span>
										<ul
											style={{
												margin: "4px 0 0 16px",
											}}
										>
											{entries.map(([amount, count]) => (
												<li key={amount}>
													${amount}: {count}
												</li>
											))}
										</ul>
										<div
											style={{
												marginLeft: 16,
												fontStyle: "italic",
											}}
										>
											Normal Format (last 7d):{" "}
											{normal.format}
											{normal.firstDigits
												? `, first digits: ${normal.firstDigits}`
												: ""}
										</div>
									</div>
								);
							}),
						)
					).filter(Boolean);

					// Push a row to your existing warningReport table
					setWarningReport((prev) => {
						const updated = [
							...prev,
							{
								affiliateId: affiliate_Id,
								affiliateName: affiliate_Name,
								numMerchants: merchants.length,
								totalOrderCount,
								hasDiff: rowHasDiff,
								jsx: (
									<div key={affiliate_Id}>
										{merchantBlocks}
									</div>
								),
							},
						];

						// sort: red first, then most orders first
						return updated.sort((a, b) => {
							if (a.hasDiff !== b.hasDiff)
								return b.hasDiff - a.hasDiff;
							return b.totalOrderCount - a.totalOrderCount;
						});
					});
				} else {
					reviewList[affiliate_Id] = {
						totalMerchants: Object.keys(merchantMap).length,
						totalOrders: result.length,
					};
				}
			}
			setLoading("false");

			console.log(warningReport);

			console.log("Review List:", reviewList);
		} catch (err) {
			setError(
				"Error while processing the file. Check format or console for debug info.",
			);
			console.error(err);
		}
		setStage("report");
	};
	const runOutlierReport = async (e) => {
		//TODO set update this to be its own seperate script file.
		const settings = await getSettings();
		setLoading(true);
		console.log(startDate, endDate);
		const currentDates = getReportTexts(startDate, endDate);
		const mList = [];
		const warningData = [];
		const normalFormatCache = {};
		const affList = await runAPI(
			{
				report_id: 15,
				startDate: getDefaultStartDate("last30days"),
				endDate,
				merchantId: 0,
			},
			settings.key,
			"NULL",
		);

		try {
			const filtered = affList.filter((row) => {
				const affId = parseInt(row["Affiliate Id"]);
				const numSales = parseFloat(row["# of Sales"]);
				const tags = row["Affiliate Tags"] || "";
				const inExclusionList = exclusionList.includes(affId);
				const avgSale = parseFloat(row["Average Sale Amount"]);
				const recruited = tags
					.trim()
					.startsWith("Affiliate Recruiting");

				return (
					!isNaN(affId) &&
					affId >= minimumID &&
					!isNaN(numSales) &&
					numSales > 1 &&
					!isNaN(avgSale) &&
					avgSale > minimumSaleAvg &&
					!inExclusionList &&
					(!excludeRecruited || !recruited)
				);
			});
			const trimmed = filtered.map((row) => ({
				Affiliate_Id: row["Affiliate Id"],
				Affiliate: row["Affiliate"],
				Affiliate_Name: row["Affiliate Name"],
				Click_Throughs: row["Click Throughs"],
				Sales: row["Sales"],
				Num_Sales: row["# of Sales"],
				Conversion_Rate: row["Conversion Rate"],
				Average_Sale_Amount: row["Average Sale Amount"],
			}));

			setError(null);
			const warningList = {};
			const reviewList = {};

			for (let i = 0; i < trimmed.length; i++) {
				const affiliate_Id = trimmed[i].Affiliate_Id;
				const affiliate_Name = trimmed[i].Affiliate_Name;
				const result = await runAPI(
					{
						report_id: 8,
						startDate: startDate,
						endDate: endDate,
						affiliate_id: affiliate_Id,
					},
					settings.key,
					"NULL",
				);
				// TODO
				console.log(`Result for Affiliate ID ${affiliate_Id}:`, result);
				const merchantMap = {};

				//AFFAPIRETURN
				result.forEach((order) => {
					const merchantId = order["Merchant Id"];
					if (!mList.includes(merchantId)) {
						mList.push(merchantId);
					}

					const amount = parseFloat(order["Transaction Amount"]);
					const itemCount = parseInt(order["Item Count"]);
					const orderId = order["Order Id"];

					if (!merchantMap[merchantId]) {
						merchantMap[merchantId] = {
							orders: [],
							susTotals: {},
						};
					}

					merchantMap[merchantId].orders.push(orderId);

					const key = `${amount.toFixed(2)}|${itemCount}`;
					merchantMap[merchantId].susTotals[key] =
						(merchantMap[merchantId].susTotals[key] || 0) + 1;
				});

				let hasSuspicious = false;
				let totalMatchCount = 0;

				Object.entries(merchantMap).forEach(([merchantId, data]) => {
					Object.entries(data.susTotals).forEach(([key, count]) => {
						if (count >= 3) {
							hasSuspicious = true;
							totalMatchCount += count;
						}
					});
				});

				if (hasSuspicious) {
					const merchants = summarizeByMerchant(result);

					// Build blocks with NORMAL format appended (last 7d),
					// and style red+bold if different from the affiliate’s observed format.
					let rowHasDiff = false;
					let totalOrderCount = 0;

					const merchantBlocks = (
						await Promise.all(
							merchants.map(async (m) => {
								const entries = Array.from(
									m.amountCounts.entries(),
								)
									.filter(([, count]) => count >= 2)
									.sort((a, b) => b[1] - a[1]);

								if (entries.length === 0) return null;

								// get normal format
								let normal = normalFormatCache[m.merchantId];
								if (!normal) {
									try {
										normal =
											await fetchMerchantNormalFormat({
												merchantId: m.merchantId,
												settings,
												endDate,
											});
										normalFormatCache[m.merchantId] =
											normal;
									} catch {
										normal = {
											format: "unknown",
											firstDigits: "",
										};
									}
								}

								const isDiff =
									(normal?.format || "unknown") !==
									(m?.format || "unknown");
								if (isDiff) rowHasDiff = true;

								// accumulate total orders (count of all amounts)
								totalOrderCount += entries.reduce(
									(sum, [, count]) => sum + count,
									0,
								);

								return (
									<div
										key={m.merchantId}
										style={{
											marginBottom: 8,
											fontWeight: isDiff
												? 700
												: undefined,
											color: isDiff
												? "#c62828"
												: undefined,
										}}
									>
										<span>
											<strong>{m.merchantId}</strong> —{" "}
											{m.merchantName} &nbsp;
											<em>
												(Observed Format: {m.format}
												{m.firstDigits
													? `, first digits: ${m.firstDigits}`
													: ""}
												)
											</em>
										</span>
										<ul
											style={{
												margin: "4px 0 0 16px",
											}}
										>
											{entries.map(([amount, count]) => (
												<li key={amount}>
													${amount}: {count}
												</li>
											))}
										</ul>
										<div
											style={{
												marginLeft: 16,
												fontStyle: "italic",
											}}
										>
											Normal Format (last 7d):{" "}
											{normal.format}
											{normal.firstDigits
												? `, first digits: ${normal.firstDigits}`
												: ""}
										</div>
									</div>
								);
							}),
						)
					).filter(Boolean);

					// Push a row to your existing warningReport table
					setWarningReport((prev) => {
						const updated = [
							...prev,
							{
								affiliateId: affiliate_Id,
								affiliateName: affiliate_Name,
								numMerchants: merchants.length,
								totalOrderCount,
								hasDiff: rowHasDiff,
								jsx: (
									<div key={affiliate_Id}>
										{merchantBlocks}
									</div>
								),
							},
						];

						// sort: red first, then most orders first
						return updated.sort((a, b) => {
							if (a.hasDiff !== b.hasDiff)
								return b.hasDiff - a.hasDiff;
							return b.totalOrderCount - a.totalOrderCount;
						});
					});
				} else {
					reviewList[affiliate_Id] = {
						totalMerchants: Object.keys(merchantMap).length,
						totalOrders: result.length,
					};
				}
			}
			setLoading("false");

			console.log(warningReport);

			console.log("Review List:", reviewList);
		} catch (err) {
			setError(
				"Error while processing the file. Check format or console for debug info.",
			);
			console.error(err);
		}
		setStage("report");
	};

	return (
		<div>
			<Container className="shadow-sm p-4 bg-white border position-relative card-drop-in  rounded mt-5">
				{stage === "input" && (
					<Row>
						<h3>MARK K REPORT</h3>
						<h6> Time selection. </h6>
						The following FORM is selecting what to filter out of
						the review criteria.
						<hr></hr>
						<Form.Group className="mb-3">
							<DateRangePicker
								startDate={startDate}
								endDate={endDate}
								onStartChange={setStartDate}
								onEndChange={setEndDate}
								otherFunction={console.log("x")}
							/>
							<Form.Label>
								Affiliate IDs Under:{" "}
								<strong>{minimumID}</strong>
							</Form.Label>
							<Form.Range
								min={0}
								max={500000}
								step={50000}
								value={minimumID}
								onChange={(e) => setMinimumID(e.target.value)}
							/>
						</Form.Group>
						<Form.Group className="mb-3">
							<Form.Label>
								Average Orders Under:{" "}
								<strong>${minimumSaleAvg}.00</strong>
							</Form.Label>
							<Form.Range
								min={0}
								max={1000}
								step={5}
								value={minimumSaleAvg}
								onChange={(e) =>
									setMinimumSaleAvg(e.target.value)
								}
							/>
						</Form.Group>
						<Form.Group
							className="mb-3"
							controlId="excludeRecruited"
						>
							<Form.Check
								type="checkbox"
								label="Exclude Recruited Affiliates"
								checked={excludeRecruited}
								onChange={(e) =>
									setExcludeRecruited(e.target.checked)
								}
							/>
						</Form.Group>
						<Button
							variant="success"
							onClick={runMarkKReport}
							disabled={loading}
						>
							{loading ? "Running..." : "Run Mark K Report"}
						</Button>
						{error && <Alert variant="danger">{error}</Alert>}
						{loading && (
							<LoadingOverlay
								modules={filteredData}
								completedModules={completedModules}
								loadingStage={loadingStage}
								merchantReference={11177}
								tableButton={tableButton}
							/>
						)}
					</Row>
				)}
				{stage === "report" && (
					<Row>
						<ColumnMapTable
							title={"WARNING LIST"}
							tableMap={[
								{ label: "Affiliate ID", type: "string" },
								{ label: "Affiliate Name", type: "string" },
								{ label: "Num Merchants", type: "string" },
								{
									label: "Merchant Report Format",
									type: "string",
								},
							]}
							table={warningReport.map((r) => [
								r.affiliateId,
								r.affiliateName,
								String(r.numMerchants),
								r.jsx,
							])}
							id={123456}
							hideTools={true}
							classes={["largeFont"]}
						/>
					</Row>
				)}
			</Container>
			<Container className="shadow-sm p-4 bg-white border position-relative card-drop-in  rounded mt-5">
				{stage === "input" && (
					<Row>
						<h3>Atypical Orders </h3>
						<h6> Time selection. </h6>
						The following FORM is selecting what to filter out of
						the review criteria.
						<hr></hr>
						<Form.Group className="mb-3">
							<DateRangePicker
								startDate={startDate}
								endDate={endDate}
								onStartChange={setStartDate}
								onEndChange={setEndDate}
								otherFunction={console.log("x")}
							/>
						</Form.Group>
						<Form.Group className="mb-3">
							<hr />
							<h6>
								report will look for orders over the following
								benchmark
							</h6>
							<Form.Label>
								Outlier Benchmark:{" "}
								<strong>${outlierBenchmark}.00</strong>
							</Form.Label>
							<Form.Range
								min={1000}
								max={10000}
								step={5}
								value={outlierBenchmark}
								onChange={(e) =>
									setOutlierBenchmark(e.target.value)
								}
							/>
						</Form.Group>
						<Button
							variant="success"
							onClick={runOutlierReport}
							disabled={loading}
						>
							{loading ? "Running..." : "Run Outlier Report"}
						</Button>
						{error && <Alert variant="danger">{error}</Alert>}
						{loading && (
							<LoadingOverlay
								modules={filteredData}
								completedModules={completedModules}
								loadingStage={loadingStage}
								merchantReference={11177}
								tableButton={tableButton}
							/>
						)}
					</Row>
				)}
				{stage === "report" && (
					<Row>
						<ColumnMapTable
							title={"WARNING LIST"}
							tableMap={[
								{ label: "Affiliate ID", type: "string" },
								{ label: "Affiliate Name", type: "string" },
								{ label: "Num Merchants", type: "string" },
								{
									label: "Merchant Report Format",
									type: "string",
								},
							]}
							table={warningReport.map((r) => [
								r.affiliateId,
								r.affiliateName,
								String(r.numMerchants),
								r.jsx,
							])}
							id={123456}
							hideTools={true}
							classes={["largeFont"]}
						/>
					</Row>
				)}
			</Container>
		</div>
	);
};

export default MarkKalbachReport;
